<script setup lang="ts">
/*
 * Asking for the project name at the moment a price-list project comes into being (31.08.2026,
 * customer request). Before this, a project was created with a generated name and renamed
 * afterwards through the pencil next to it — which nobody did, so "My Projects" filled up with
 * "Untitled project – <date>".
 *
 * The two moments it is shown are exactly the two moments auto-save arms: a "Custom price list"
 * when it is started, a template project when "Enable edit" is pressed. A project that already exists on
 * the server has its name and is never asked again.
 *
 * The field starts on the generated name rather than empty: it is a suggestion that can be taken as
 * it is, and it also shows what the name would have been. Cancel creates nothing — see the call
 * sites; that is why this component only reports the decision and holds no state of its own.
 */
import { ut_getText } from '@base/utils/utils'

const p = defineProps<{
  open: boolean
  // The generated name the field starts on.
  suggestion: string
}>()

const emit = defineEmits<{
  confirm: [string]
  cancel: []
}>()

const draft: Ref<string> = ref('')

// Filled on opening, not on mount: the modal stays mounted between two projects, and the second one
// has to start on its own suggestion instead of what the first one was left with.
watch(() => p.open, (open) => {
  if (open) { draft.value = p.suggestion }
}, { immediate: true })

const name: ComputedRef<string> = computed(() => draft.value.trim())

function confirm(): void {
  if (!name.value) { return }
  emit('confirm', name.value)
}
</script>

<template>
  <UModal
    :open="open"
    :title="ut_getText('txt_pricelist-name-title')"
    :ui="useAppConfig().modalFrame.auto"
    @update:open="(isOpen) => { if (!isOpen) { emit('cancel') } }"
  >
    <template #body>
      <!-- `modalFrame.auto` shrink-wraps, so the field brings its own width — the same one the
           in-place rename in Cart.vue uses, which is where the name is edited afterwards. -->
      <UInput
        v-model="draft"
        autofocus
        class="w-96 max-w-full"
        @keyup.enter="confirm()"
      />
      <div class="flex justify-end gap-2 mt-6">
        <UButton variant="ghost" :label="ut_getText('btn_cancel')" @click="emit('cancel')" />
        <!-- An empty name would leave a nameless tile in the management, so it is refused here
             rather than silently replaced by the suggestion again. -->
        <UButton :disabled="!name" :label="ut_getText('btn_create')" @click="confirm()" />
      </div>
    </template>
  </UModal>
</template>
